import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { PAYMENT_METHODS } from '@constants/paymentMethods'
import { PAYMENT_PROVIDERS } from '@constants/paymentProviders'
import { PaymentMethodStatus } from '@enums'
import { SubmitEventPayload, ConfirmPaymentParams } from '@hooks/useBookingFlow'
import { collectDlocalDeviceFingerprint } from '@lib/dlocalDeviceFingerprint'
import collectMercadoPagoDeviceId from '@lib/mercadoPagoDeviceId'
import { PaymentMethod } from '@pages/Checkout/hooks/Payment/useBookingPayment'
import PaymentLabel from '@pages/Checkout/Payment/Label'

export const useMercadoPago = (): PaymentMethod => {
  const [status, setStatus] = useState(PaymentMethodStatus.Pending)
  const [deviceEventUuid, setDeviceEventUuid] = useState<string | null>(null)

  useEffect(() => {
    const init = async () => {
      try {
        const [eventUuid] = await Promise.all([collectDlocalDeviceFingerprint(), collectMercadoPagoDeviceId()])
        setDeviceEventUuid(eventUuid)
        setStatus(PaymentMethodStatus.Ready)
      } catch {
        setStatus(PaymentMethodStatus.Rejected)
      }
    }
    init()
  }, [])

  const submitForm = useCallback((): SubmitEventPayload => {
    return {
      paymentMethodData: {
        type: 'MP',
        flow: 'REDIRECT',
        country: 'MX',
        return_url: typeof window !== 'undefined' ? window.location.href : '',
      },
      deviceId: (window as Window & { MP_DEVICE_SESSION_ID?: string }).MP_DEVICE_SESSION_ID,
      ...(deviceEventUuid && { deviceEventUuid }),
    }
  }, [deviceEventUuid])

  const confirmPayment = useCallback((response: ConfirmPaymentParams) => {
    const actionData = response.details?.action as { redirect?: string }
    if (actionData?.redirect) {
      window.location.href = actionData.redirect
    }
  }, [])

  return useMemo(
    () => ({
      status,
      getOption: () => ({
        value: PAYMENT_METHODS.MERCADO_PAGO,
        label: <PaymentLabel type={PAYMENT_METHODS.MERCADO_PAGO} />,
      }),
      provider: PAYMENT_PROVIDERS.DLOCAL,
      on: { submitForm, confirmPayment },
    }),
    [status, submitForm, confirmPayment],
  )
}

export default useMercadoPago
